import { get, isEmpty } from 'lodash-es';
import { getRatingScale, getOverallRating, getPlainReviewBody } from 'site-modules/shared/utils/editorial-helper';
import { buildDisplayVehicle } from 'site-modules/shared/utils/core-page/display-name-builders';
import { parseContent } from 'client/data/cms/content';
import { getValidImageUrl, SOURCES } from 'client/utils/image-helpers';
import { getOrganizationSchema } from './organization';
import { getPersonSchema } from './person';

export function getAggregateRatingFromCoreConsumerReviews(consumerReviews) {
  const consumerRatingValue =
    get(consumerReviews, 'ratingAggregation.averageStars') || get(consumerReviews, 'averageUserRating');
  const consumerReviewsCount =
    get(consumerReviews, 'totalReviews') || get(consumerReviews, 'totalNumber') || get(consumerReviews, 'totalCount');

  return consumerRatingValue
    ? {
        '@type': 'AggregateRating',
        reviewCount: consumerReviewsCount || 1,
        ratingValue: consumerRatingValue && consumerRatingValue.toFixed(1),
        bestRating: 5,
        worstRating: 1,
      }
    : undefined;
}

export function getReviewedItem({ makeModelSubmodelYear, consumerReviews }) {
  const make = get(makeModelSubmodelYear, 'make.name', '');
  const model = get(makeModelSubmodelYear, 'model.name', '');
  const year = get(makeModelSubmodelYear, 'year', '');

  return {
    itemReviewed: {
      '@type': 'Car',
      name: `${year} ${make} ${model}`,
      model,
      vehicleModelDate: year,
      brand: {
        '@type': 'Thing',
        name: make,
      },
      aggregateRating: getAggregateRatingFromCoreConsumerReviews(consumerReviews),
    },
  };
}

const getNotesList = items => ({
  '@type': 'ItemList',
  itemListElement: items.map((name, ind) => ({
    '@type': 'ListItem',
    position: ind + 1,
    name,
  })),
});

/**
 * Testing tool: https://search.google.com/structured-data/testing-tool
 *
 * Schema: http://schema.org/Review
 */
export function getEditorialReviewSchema({
  params,
  makeModelSubmodelYear,
  editorialReview,
  author,
  consumerReviews,
  thumbUrl,
  customDateModified,
  hasItemReviewed = true,
  isCorePage = false,
}) {
  const editorReviewRating = getOverallRating(editorialReview);
  const bestRating = editorReviewRating ? getRatingScale(editorialReview) : 5;
  const organization = getOrganizationSchema();
  const headline = buildDisplayVehicle({
    makeModelSubmodelYear,
    params: {},
    trimDuplicateSubmodel: true,
    disableTrademarkCharacter: isCorePage,
  });
  const authorContent = isEmpty(author) ? null : parseContent(author);
  const cons = get(editorialReview, 'cons', []);
  const pros = get(editorialReview, 'pros', []);

  return {
    '@type': 'Review',
    reviewBody: getPlainReviewBody({ editorialReview, params, vehicle: makeModelSubmodelYear }),
    ...(isCorePage
      ? {
          datePublished: get(editorialReview, 'published'),
          dateModified: customDateModified || get(editorialReview, 'updated'),
        }
      : {}),
    ...(isCorePage ? { description: 'Review, Pricing, and Specs' } : {}),
    ...(isCorePage ? { headline } : {}),
    ...(isCorePage ? { thumbnailURL: thumbUrl } : {}),
    publisher: organization,
    ...(pros.length ? { positiveNotes: getNotesList(pros) } : {}),
    ...(cons.length ? { negativeNotes: getNotesList(cons) } : {}),
    author: authorContent
      ? getPersonSchema({
          name: authorContent.metadata('authorName').value(),
          jobTitle: authorContent.metadata('authorTitle').value(),
          pathname: authorContent.metadata('authorLink').value(),
          photoUrl: getValidImageUrl(authorContent.metadata('photoPath').value(), SOURCES.STATIC),
        })
      : organization,
    ...(hasItemReviewed && getReviewedItem({ makeModelSubmodelYear, consumerReviews })),
    ...(editorReviewRating && {
      reviewRating: {
        '@type': 'Rating',
        ratingValue: parseFloat(editorReviewRating).toFixed(1),
        bestRating,
        worstRating: 1,
      },
    }),
  };
}

export function getConsumerReviewSchema({
  makeModelSubmodelYear,
  consumerReviews,
  includeReviewedItem = true,
  year = '',
  model = '',
}) {
  const review = get(consumerReviews, 'reviews[0]');

  if (!review) return undefined;

  const { author, text, createTime, created, title } = review;

  return {
    '@type': 'Review',
    reviewBody: text,
    author: {
      '@type': 'Person',
      name: get(author, 'authorName')?.trim() || `Satisfied ${year} ${model} Customer`,
    },
    ...(includeReviewedItem && getReviewedItem({ makeModelSubmodelYear, consumerReviews })),
    datePublished: createTime || created,
    name: title,
  };
}

export function appendReviewSchema(
  carSchema,
  {
    params,
    makeModelSubmodelYear,
    editorialReview,
    author,
    consumerReviews,
    priceRange = [0],
    hasItemReviewed,
    thumbUrl,
    customDateModified,
    isCorePage = false,
    alwaysIncludeReview = false,
  }
) {
  const editorialRatingValue = getOverallRating(editorialReview);
  const minPrice = Math.min(...priceRange);
  const maxPrice = Math.max(...priceRange);

  const hasSchema = editorialRatingValue || editorialReview?.review || carSchema?.aggregateRating;

  return hasSchema
    ? {
        ...carSchema,
        aggregateRating: editorialRatingValue ? undefined : carSchema?.aggregateRating,
        review:
          alwaysIncludeReview || editorialRatingValue || editorialReview?.review || editorialReview?.summary
            ? {
                ...getEditorialReviewSchema({
                  params,
                  makeModelSubmodelYear,
                  editorialReview,
                  author,
                  consumerReviews,
                  priceRange,
                  hasItemReviewed,
                  thumbUrl,
                  customDateModified,
                  isCorePage,
                }),
                ...((minPrice || maxPrice) &&
                  !isCorePage && {
                    potentialAction: {
                      '@type': 'TradeAction',
                      price: minPrice || maxPrice,
                      priceCurrency: 'USD',
                    },
                  }),
              }
            : undefined,
      }
    : undefined;
}
